import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * A small, fixed table that is part of what a card or dialog says: a before-and-after comparison,
 * an import preview, the DNS records to add, a run's steps. No sorting,
 * paging, selection for bulk actions, column menus or export: a list people work through is a DataTable
 * (tables-and-lists.mdc). Muted small headers, a rule between rows, numbers right-aligned with
 * `numeric`. Cells sit flush with the card's padding; `inset` pads the outer cells too, for a table in
 * a bordered box of its own. `size="compact"` is small print with tighter rows: a legend or key
 * beside a setting. A cell's text is `tone` (`muted` for a note or explanation column, `danger` for
 * a figure that is short or overdue, `success` for one that is up) and `strong` (a row's label, the
 * figure that matters); `mono` is a code (a commit, an address); `actions` holds a row's own buttons.
 * A `numeric` cell keeps its figure on one line. A row can be `highlighted` (the one a link pointed
 * at) or `dimmed` (left out, as an unticked line in a preview). A preview whose lines are ticked in
 * or out has a `Checkbox` in its first column; that is not a DataTable's row selection.
 * A long table in a dialog scrolls in its own box: `scrollClassName` gives that box its height
 * (`max-h-[60vh]`), and the header row stays in view while it scrolls. Don't wrap the table in a
 * `max-h-… overflow-auto` div of your own.
 * Cells carry `data-numeric` and `data-tone`, and totals sit in a `tfoot`, so a printed copy of the
 * table (a summary's "Print / Save as PDF") can style them without classes of its own.
 * A cell may hold a control the preview is about: an `Input` to fill in, a group of ticks.
 * `className` only places or sizes (`hidden sm:table-cell`, `w-10`, `max-w-64 truncate`).
 */
export function SimpleTable({
  className,
  inset = false,
  size = 'default',
  scrollClassName,
  ...props
}: ComponentProps<'table'> & {
  inset?: boolean;
  size?: 'default' | 'compact';
  /** Gives the table its own scroll box of this height (`max-h-[60vh]`); its header row stays in view. */
  scrollClassName?: string;
}) {
  const sticky = scrollClassName != null;
  return (
    <div className={cn('w-full', sticky ? cn('overflow-auto', scrollClassName) : 'overflow-x-auto')}>
      <table
        data-inset={inset}
        data-size={size}
        data-sticky-header={sticky}
        className={cn(
          'group/simple-table w-full border-collapse',
          size === 'compact' ? 'text-xs' : 'text-sm',
          className
        )}
        {...props}
      />
    </div>
  );
}

export function SimpleTableHeader({ className, ...props }: ComponentProps<'thead'>) {
  return <thead className={cn('border-b text-xs text-muted-foreground', className)} {...props} />;
}

export function SimpleTableBody({ className, ...props }: ComponentProps<'tbody'>) {
  return <tbody className={cn('[&>tr]:border-b [&>tr:last-child]:border-0', className)} {...props} />;
}

/** Totals under the rows ("Balance due"), in medium weight over a rule. */
export function SimpleTableFooter({ className, ...props }: ComponentProps<'tfoot'>) {
  return <tfoot className={cn('border-t font-medium', className)} {...props} />;
}

export function SimpleTableRow({
  className,
  highlighted = false,
  dimmed = false,
  ...props
}: ComponentProps<'tr'> & {
  /** The row a link or search pointed at: a light primary tint and ring. */
  highlighted?: boolean;
  /** Left out of what the table adds up to (an unticked line in a preview): faded. */
  dimmed?: boolean;
}) {
  return (
    <tr
      className={cn(
        highlighted && 'bg-primary/5 ring-1 ring-inset ring-primary/40',
        dimmed && 'opacity-50',
        className
      )}
      {...props}
    />
  );
}

const CELL =
  'py-2 pr-3 align-top last:pr-0 group-data-[size=compact]/simple-table:py-1 group-data-[inset=true]/simple-table:px-3 group-data-[inset=true]/simple-table:first:pl-4 group-data-[inset=true]/simple-table:last:pr-4';

const TONE = {
  muted: 'text-muted-foreground',
  danger: 'text-danger',
  success: 'text-success-strong',
} as const;

export function SimpleTableHead({
  className,
  numeric = false,
  ...props
}: ComponentProps<'th'> & { numeric?: boolean }) {
  return (
    <th
      data-numeric={numeric || undefined}
      className={cn(
        CELL,
        'text-left font-medium',
        'group-data-[sticky-header=true]/simple-table:sticky group-data-[sticky-header=true]/simple-table:top-0 group-data-[sticky-header=true]/simple-table:z-10 group-data-[sticky-header=true]/simple-table:bg-background group-data-[sticky-header=true]/simple-table:shadow-[inset_0_-1px_0_var(--border)]',
        numeric && 'text-right',
        className
      )}
      {...props}
    />
  );
}

export function SimpleTableCell({
  className,
  numeric = false,
  tone,
  strong = false,
  mono = false,
  actions = false,
  ...props
}: ComponentProps<'td'> & {
  /** Right-aligned with tabular figures, for amounts and counts. */
  numeric?: boolean;
  /**
   * `muted` for a note or explanation column; `danger` for a figure that is short or overdue;
   * `success` for one that is up (a sales lift).
   */
  tone?: keyof typeof TONE;
  /** Medium weight: a row's label, or the figure that matters most in the row. */
  strong?: boolean;
  /** A code in small monospace: a commit, an email address or domain. */
  mono?: boolean;
  /** The row's own buttons (ghost `icon-xs` Edit, Remove): right-aligned, kept on one line. */
  actions?: boolean;
}) {
  return (
    <td
      data-numeric={numeric || undefined}
      data-tone={tone}
      className={cn(
        CELL,
        numeric && 'text-right tabular-nums whitespace-nowrap',
        actions && 'text-right whitespace-nowrap',
        tone && TONE[tone],
        strong && 'font-medium',
        mono && 'font-mono text-xs',
        className
      )}
      {...props}
    />
  );
}
